import { useCallback, useEffect, useRef } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { useHistory } from "react-router-dom";
import useAuthUser from "./useAuthUser";
import emitter, { EVENTS } from "lib/emitter";
import providers from "lib/providers";
import { Routes } from "router/config";

interface BadgePlugin {
  setBadgeCount(options: { count: number }): Promise<void>;
}
interface AppPlugin {
  addListener(event: string, handler: () => void): Promise<{ remove: () => void }>;
}
const LocalNotifications = registerPlugin<BadgePlugin>("LocalNotifications");
const CapApp = registerPlugin<AppPlugin>("App");

const isNative = Capacitor.isNativePlatform();
const isAndroid = isNative && Capacitor.getPlatform() === "android";

/**
 * Android-specific prefix that tags the FCM token at registration time so
 * the backend's incoming-call pipeline (CallFcmPushSender) can target only
 * Android devices with data-only high-priority FCM pushes. On iOS the token
 * is stored un-prefixed and is used purely for non-call notifications; iOS
 * incoming calls are served via APNs VoIP (see useNativeCallKit.ts). Mirrors
 * the "voip:" prefix scheme used by DefaultDeviceTokenService.
 */
const FCM_ANDROID_TOKEN_PREFIX = "fcm-android:";

function tagTokenForBackend(rawToken: string): string {
  return isAndroid ? FCM_ANDROID_TOKEN_PREFIX + rawToken : rawToken;
}

function resolvePushTarget(data: Record<string, string> | undefined): string | null {
  if (!data) return null;
  if (data.groupSlug || data.groupId) {
    const base = `${Routes.messages}/group/${data.groupSlug || data.groupId}`;
    // Group Topics (Темы): deep-link into the specific topic when present.
    return data.topicId ? `${base}/topic/${data.topicId}` : base;
  }
  if (data.senderId) return `${Routes.messages}/${data.senderId}`;
  return null;
}

export default function useNativePush() {
  const { user } = useAuthUser();
  const history = useHistory();
  const historyRef = useRef(history);
  historyRef.current = history;
  const userRef = useRef(user);
  userRef.current = user;
  const registeredRef = useRef(false);
  const tokenRef = useRef<string | null>(null);
  const tokenListenerRef = useRef<{ remove: () => void } | null>(null);
  // Buffer for a tap that fires before the user is hydrated (cold-start from a
  // killed app: OS launches the process and dispatches notificationActionPerformed
  // before useAuthUser has read the persisted session). Processed in the user
  // effect below.
  //
  // Best-effort assumption: the user that hydrates next is the same user the
  // notification was meant for. The rare exception is logout → app-kill → tap →
  // login as a DIFFERENT account, which would navigate the new user to the old
  // user's chat URL. The backend permission check rejects access in that case,
  // so the user lands on a permission-denied screen rather than seeing other
  // users' data — acceptable for an edge case.
  const pendingTapRef = useRef<string | null>(null);

  // Tap + badge listeners — registered ON MOUNT, independent of auth. Capacitor
  // does NOT buffer listener events that fire before a handler is attached, so
  // putting these inside the auth-dependent registerPush() callback used to
  // drop cold-start taps on the floor. The handler defers navigation via
  // pendingTapRef when user/history aren't ready yet.
  useEffect(() => {
    if (!isNative) return;
    let cancelled = false;
    let tapHandle: { remove: () => void } | null = null;
    let recvHandle: { remove: () => void } | null = null;

    FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
      const target = resolvePushTarget(event.notification.data as Record<string, string> | undefined);
      if (!target) return;
      if (userRef.current) {
        historyRef.current.push(target);
      } else {
        pendingTapRef.current = target;
      }
    }).then((h) => { if (cancelled) h.remove(); else tapHandle = h; }).catch(() => {});

    FirebaseMessaging.addListener("notificationReceived", (event) => {
      const data = event.notification.data as Record<string, string> | undefined;
      if (data?.notificationType === "badgeUpdate") {
        const count = parseInt(data.badgeCount ?? "0", 10);
        LocalNotifications.setBadgeCount({ count }).catch(() => {});
      }
    }).then((h) => { if (cancelled) h.remove(); else recvHandle = h; }).catch(() => {});

    return () => {
      cancelled = true;
      tapHandle?.remove?.();
      recvHandle?.remove?.();
    };
  }, []);

  // Drain the cold-start tap buffer once the user is hydrated.
  useEffect(() => {
    if (user && pendingTapRef.current) {
      historyRef.current.push(pendingTapRef.current);
      pendingTapRef.current = null;
    }
  }, [user]);

  const registerPush = useCallback(async () => {
    if (!isNative || !user || registeredRef.current) return;

    try {
      const perm = await FirebaseMessaging.requestPermissions();
      if (perm.receive !== "granted") {
        console.warn("[NativePush] Permission not granted:", perm.receive);
        return;
      }

      const { token } = await FirebaseMessaging.getToken();
      console.log("[NativePush] FCM token:", token.substring(0, 20) + "...");
      const tagged = tagTokenForBackend(token);
      tokenRef.current = tagged;

      try {
        await providers.PushNotificationsProvider.pushNotificationsAddToken({
          data: { token: tagged, userId: user.id },
        });
        console.log("[NativePush] Token sent to backend" + (isAndroid ? " (android-tagged)" : ""));
      } catch (err) {
        console.error("[NativePush] Failed to send token:", err);
      }

      // Store the handle so cleanup() can remove just this listener on logout
      // without touching the mount-bound tap/badge listeners (which must
      // survive logout so a tap of an old notification still drains via
      // pendingTapRef on next login).
      const handle = await FirebaseMessaging.addListener("tokenReceived", async (event) => {
        console.log("[NativePush] FCM token refreshed:", event.token.substring(0, 20) + "...");
        const retagged = tagTokenForBackend(event.token);
        tokenRef.current = retagged;
        try {
          await providers.PushNotificationsProvider.pushNotificationsAddToken({
            data: { token: retagged, userId: user.id },
          });
        } catch (err) {
          console.error("[NativePush] Failed to send refreshed token:", err);
        }
      });
      tokenListenerRef.current = handle;

      registeredRef.current = true;
      console.log("[NativePush] Initialized");
    } catch (e) {
      console.error("[NativePush] Init failed:", e);
    }
  }, [user]);

  const cleanup = useCallback(async () => {
    if (!isNative) return;
    try {
      if (tokenRef.current && user) {
        await providers.PushNotificationsProvider.pushNotificationsRemoveToken({
          data: { token: tokenRef.current, userId: user.id },
        });
      }
      // Remove only the tokenReceived listener — the tap/badge listeners are
      // mount-scoped and intentionally survive logout/login cycles.
      tokenListenerRef.current?.remove?.();
      tokenListenerRef.current = null;
      registeredRef.current = false;
      tokenRef.current = null;
      console.log("[NativePush] Cleaned up");
    } catch (e) {
      console.error("[NativePush] Cleanup failed:", e);
    }
  }, [user]);

  // Register when user is authenticated
  useEffect(() => {
    if (user && isNative) {
      registerPush();
    }
  }, [user, registerPush]);

  // Clear badge + notification center when app comes to foreground
  useEffect(() => {
    if (!isNative) return;

    const clearBadge = () => {
      LocalNotifications.setBadgeCount({ count: 0 }).catch(() => {});
      FirebaseMessaging.removeAllDeliveredNotifications().catch(() => {});
    };

    let handle: { remove: () => void } | undefined;
    CapApp.addListener("resume", clearBadge)
      .then((h) => { handle = h; })
      .catch(() => {});
    clearBadge(); // also clear on initial mount
    return () => { handle?.remove?.(); };
  }, []);

  // Cleanup on logout
  useEffect(() => {
    const onLoginDataChange = (data: any) => {
      if (!data?.user) cleanup();
    };
    emitter.on(EVENTS.SET_LOGIN_DATA, onLoginDataChange);
    return () => {
      emitter.off(EVENTS.SET_LOGIN_DATA, onLoginDataChange);
    };
  }, [cleanup]);
}
